/**
 * Data Asset Intelligence Service Layer (TypeScript)
 *
 * Consolidated, optimized API client following dai_node_ref patterns:
 * - Generic domain-aware functions instead of per-domain duplicates
 * - Action-based PUT bodies (e.g., schedule rule edit via `action: 'update'`)
 * - Unified asset model across Silver, Gold, Iberia
 * - Background job pattern (async operations via POST /jobs + polling)
 *
 * All requests are wrapped in a `{ ok, status, data, error }` envelope.
 */

import axios from 'axios';
import { url } from 'service/config';
import { getAuthorazieHeader } from 'service/service';

const BASE = `${url}/dataAssetIntelligence`;

export interface ApiResult<T = any> {
	ok: boolean;
	status: number;
	data: T;
	error?: string;
}

// ─── Domain types ───────────────────────────────────────────────────────────

export type Domain = 'silver' | 'gold' | 'iberia';

export interface Asset {
	asset_id: string;
	asset_name: string;
	display_name?: string;
	market?: string;
	sector?: string;
	application?: string;
	status?: string;
	run_count?: number;
	runs?: AssetRun[];
	volume?: VolumeData;
}

export interface AssetRun {
	run_number: number;
	status: string;
	expected_start?: string;
	expected_end?: string;
	sla_breach_time?: string;
	actual_start?: string;
	actual_end?: string;
	duration_mins?: number;
	sla_met?: boolean;
	exec_status?: string;
	error_message?: string;
	status_detail?: string;
}

export interface VolumeData {
	avg_records_in?: number;
	avg_records_out?: number;
	actual_records_in?: number;
	actual_records_out?: number;
	computed_at?: string;
}

export interface ScheduleRule {
	rule_id?: number;
	asset_id?: string;
	asset_name?: string;
	display_name?: string;
	day_of_week?: string;
	slot_index?: number;
	expected_start?: string;
	expected_end?: string;
	sla_breach_time?: string;
	source?: string;
	is_active?: boolean;
	user_notes?: string;
}

export interface JobStatus {
	job_id: string;
	type: string;
	status: 'pending' | 'running' | 'completed' | 'failed';
	progress?: number;
	error?: string;
	result?: any;
	created_at?: string;
	completed_at?: string;
}

// ─── Internal request helper ────────────────────────────────────────────────

type Params = Record<string, any> | undefined;

async function request(
	method: 'get' | 'post' | 'put',
	path: string,
	{ params, body }: { params?: Params; body?: any } = {}
): Promise<ApiResult> {
	try {
		const response = await axios({
			method,
			url: `${BASE}${path}`,
			params,
			data: body,
			headers: getAuthorazieHeader()
		});
		return { ok: true, status: response.status, data: response.data?.data ?? response.data };
	} catch (err: any) {
		return {
			ok: false,
			status: err?.response?.status ?? 0,
			data: err?.response?.data?.data ?? null,
			error: err?.response?.data?.data?.message || err?.message || 'Request failed'
		};
	}
}

const get = (path: string, params?: Params) => request('get', path, { params });
const post = (path: string, body?: any) => request('post', path, { body });
const put = (path: string, body?: any) => request('put', path, { body });

// ─── Silver API ──────────────────────────────────────────────────────────────

export const silver = {
	/**
	 * Get the Silver dashboard payload for a date (flat, already row-shaped items).
	 */
	getStatus: (date?: string): Promise<ApiResult<{ items: any[]; summary: Record<string, number>; last_refresh: string; total_runs_today: number; db_live: boolean }>> =>
		get('/silver', date ? { date } : undefined),

	/**
	 * Get single silver asset detail with volume
	 */
	getAssetDetail: (assetName: string, date?: string): Promise<ApiResult<Asset>> =>
		get('/silver/asset/:name'.replace(':name', encodeURIComponent(assetName)), { date }),

	/**
	 * Get schedule rules with optional filters
	 */
	getScheduleRules: (params?: { asset?: string; day?: string; source?: string }): Promise<ApiResult<{ rules: ScheduleRule[] }>> =>
		get('/silver/schedule', params),

	/**
	 * Update a schedule rule via action (update|validate|reset|toggle)
	 */
	updateScheduleRule: (
		ruleId: number,
		action: 'update' | 'validate' | 'reset' | 'toggle',
		body: Partial<ScheduleRule>
	): Promise<ApiResult<ScheduleRule>> =>
		put(`/silver/schedule/${ruleId}`, { action, ...body }),

	/**
	 * Get 90-day history with ML stats
	 */
	getHistory: (params?: { asset?: string; days?: number }): Promise<ApiResult<any>> =>
		get('/silver/history', params),

	/**
	 * Get volume stats (consolidated from 3 old endpoints)
	 */
	getVolumeStats: (params?: Params): Promise<ApiResult<any>> =>
		get('/silver/volume-stats', params),

	/**
	 * Get volume prediction (ML forecast)
	 */
	getVolumePrediction: (params?: Params): Promise<ApiResult<any>> =>
		get('/silver/volume-prediction', params),

	/**
	 * Get report data (flat rows for CSV export)
	 */
	getReport: (date?: string): Promise<ApiResult<any>> =>
		get('/report', { domain: 'silver', date })
};

// ─── Gold API ────────────────────────────────────────────────────────────────

export const gold = {
	/**
	 * Get the Gold dashboard payload for a date/market (flat, already row-shaped items).
	 */
	getStatus: (date?: string, market?: string): Promise<ApiResult<{ items: any[]; summary: Record<string, number>; run_date: string; day_of_week: string; pulled_at: string; db_live: boolean }>> =>
		get('/gold', { date, market }),

	/**
	 * Get single gold job detail with upstream/downstream
	 */
	getJobDetail: (jobName: string, date?: string, market?: string): Promise<ApiResult<Asset>> =>
		get('/gold/asset/:name'.replace(':name', encodeURIComponent(jobName)), { date, market }),

	/**
	 * Get schedule rules with optional filters
	 */
	getScheduleRules: (params?: { asset?: string; day?: string; market?: string }): Promise<ApiResult<{ rules: ScheduleRule[] }>> =>
		get('/gold/schedule', params),

	/**
	 * Update a schedule rule via action
	 */
	updateScheduleRule: (
		ruleId: number,
		action: 'update' | 'validate' | 'reset' | 'toggle',
		body: Partial<ScheduleRule>
	): Promise<ApiResult<ScheduleRule>> =>
		put(`/gold/schedule/${ruleId}`, { action, ...body }),

	/**
	 * Get all markets
	 */
	getMarkets: (): Promise<ApiResult<string[]>> =>
		get('/gold/markets'),

	/**
	 * Get report data (flat rows for CSV export)
	 */
	getReport: (params?: { date?: string; market?: string }): Promise<ApiResult<any>> =>
		get('/report', { domain: 'gold', ...params })
};

// ─── Iberia API ─────────────────────────────────────────────────────────────

export const iberia = {
	/**
	 * Get the Iberia dataset rollup for a date (nested stages/jobs per dataset,
	 * NOT flat rows — Iberia.tsx's own toIberiaRows flattens this).
	 */
	getStatus: (date?: string): Promise<ApiResult<{ date: string; datasets: any[]; config_synced_at?: string; logs_synced_at?: string; db_live: boolean }>> =>
		get('/iberia', date ? { date } : undefined),

	/**
	 * Get single iberia dataset detail with stage runs and date overrides
	 */
	getDatasetDetail: (datasetName: string, date?: string): Promise<ApiResult<Asset>> =>
		get('/iberia/asset/:name'.replace(':name', encodeURIComponent(datasetName)), { date }),

	/**
	 * Get schedule rules with optional filters
	 */
	getScheduleRules: (params?: { asset?: string }): Promise<ApiResult<{ rules: ScheduleRule[] }>> =>
		get('/iberia/schedule', params),

	/**
	 * Update a schedule rule via action
	 */
	updateScheduleRule: (
		ruleId: number,
		action: 'update' | 'validate' | 'reset' | 'toggle',
		body: Partial<ScheduleRule>
	): Promise<ApiResult<ScheduleRule>> =>
		put(`/iberia/schedule/${ruleId}`, { action, ...body }),

	/**
	 * Add/remove a date override for an asset
	 */
	updateDateOverride: (
		assetId: string,
		action: 'add' | 'remove',
		overrideData: {
			specific_date: string;
			is_expected: boolean;
			user_notes?: string;
		}
	): Promise<ApiResult<any>> =>
		put(`/iberia/schedule/${assetId}/overrides`, { action, ...overrideData }),

	/**
	 * Get report data (flat rows for CSV export)
	 */
	getReport: (date?: string): Promise<ApiResult<any>> =>
		get('/report', { domain: 'iberia', date })
};

// ─── Generic Assets API (replaces per-domain asset-tagging) ──────────────────

export const assets = {
	/**
	 * Get assets for a domain with optional filters
	 */
	getList: (domain: Domain, params?: { application?: string; monitored?: boolean; critical?: boolean }): Promise<ApiResult<Asset[]>> =>
		get('/assets', { domain, ...params }),

	/**
	 * Update an asset (is_monitored, is_critical, application, display_name, etc.)
	 */
	update: (
		assetId: string,
		updates: {
			is_monitored?: boolean;
			is_critical?: boolean;
			application?: string;
			display_name?: string;
		}
	): Promise<ApiResult<Asset>> =>
		put(`/assets/${assetId}`, updates),

	/**
	 * Sync assets for a domain from source system
	 */
	sync: (domain: Domain): Promise<ApiResult<{ synced_count: number }>> =>
		post(`/assets/sync`, { domain })
};

// ─── Background Jobs API (replaces synchronous refresh endpoints) ────────────

export const jobs = {
	/**
	 * Create and queue a background job
	 */
	create: (params: {
		type: 'ml_recompute' | 'volume_stats' | 'cdc_refresh';
		domain?: Domain;
		asset_id?: string;
	}): Promise<ApiResult<{ job_id: string }>> =>
		post(`/jobs`, params),

	/**
	 * Poll job status
	 */
	getStatus: (jobId: string): Promise<ApiResult<JobStatus>> =>
		get(`/jobs/${jobId}`)
};

// ─── Utility ─────────────────────────────────────────────────────────────────

/**
 * Try one of several API calls until one succeeds (for backward compat fallback)
 */
export async function tryAny<T>(...calls: Promise<ApiResult<T>>[]): Promise<ApiResult<T>> {
	const errors: Error[] = [];
	for (const call of calls) {
		try {
			const result = await call;
			if (result.ok) return result;
			errors.push(new Error(result.error || 'API returned not-ok'));
		} catch (err) {
			errors.push(err as Error);
		}
	}
	return {
		ok: false,
		status: 0,
		data: null as unknown as T,
		error: `All attempts failed: ${errors.map((e) => e.message).join('; ')}`
	};
}

export default {
	silver,
	gold,
	iberia,
	assets,
	jobs
};
